import { lazy, Suspense } from 'react';
import { Spin } from 'antd';
import { createBrowserRouter } from 'react-router-dom';
import { RequireSession } from '@/features/auth/components/RequireSession';

const AuthenticatedLayout = lazy(
  () => import('@/app/layouts/AuthenticatedLayout'),
);
const DashboardPage = lazy(
  () => import('@/features/dashboard/pages/DashboardPage'),
);
const BranchesPage = lazy(
  () => import('@/features/branches/pages/BranchesPage'),
);
const BranchDetailPage = lazy(
  () => import('@/features/branches/pages/BranchDetailPage'),
);
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));
const UserDetailPage = lazy(
  () => import('@/features/users/pages/UserDetailPage'),
);
const AuditLogPage = lazy(() => import('@/features/audit/pages/AuditLogPage'));
const SalesReturnsPage = lazy(
  () => import('@/features/sales-returns/pages/SalesReturnsPage'),
);
const DiscountsPage = lazy(
  () => import('@/features/discounts/pages/DiscountsPage'),
);
const UnbalancePage = lazy(
  () => import('@/features/unbalance/pages/UnbalancePage'),
);
const DailyChecklistsPage = lazy(
  () => import('@/features/daily-checklists/pages/DailyChecklistsPage'),
);
const AreaManagerReportPage = lazy(
  () => import('@/features/daily-checklists/pages/AreaManagerReportPage'),
);
const DriverChecklistsPage = lazy(
  () => import('@/features/driver-checklists/pages/DriverChecklistsPage'),
);
const PettyCashPage = lazy(
  () => import('@/features/petty-cash/pages/PettyCashPage'),
);
const StaffEvaluationsPage = lazy(
  () => import('@/features/staff-evaluations/pages/StaffEvaluationsPage'),
);
const StockChecksPage = lazy(
  () => import('@/features/stock-checks/pages/StockChecksPage'),
);
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@/app/pages/NotFoundPage'));
const RouteErrorPage = lazy(() => import('@/app/pages/RouteErrorPage'));
const NoAccessPage = lazy(() => import('@/app/pages/NoAccessPage'));

function LoadingRoute() {
  return (
    <div className="full-page-status" role="status" aria-label="Loading page">
      <Spin size="large" />
    </div>
  );
}

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<LoadingRoute />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
    errorElement: withSuspense(<RouteErrorPage />),
  },
  {
    element: <RequireSession />,
    errorElement: withSuspense(<RouteErrorPage />),
    children: [
      {
        element: withSuspense(<AuthenticatedLayout />),
        children: [
          { index: true, element: withSuspense(<DashboardPage />) },
          {
            path: 'no-access',
            element: withSuspense(<NoAccessPage />),
          },
          {
            path: 'branches',
            element: withSuspense(<BranchesPage />),
          },
          {
            path: 'branches/:branchId',
            element: withSuspense(<BranchDetailPage />),
          },
          { path: 'users', element: withSuspense(<UsersPage />) },
          {
            path: 'users/:userId',
            element: withSuspense(<UserDetailPage />),
          },
          { path: 'audit-log', element: withSuspense(<AuditLogPage />) },
          {
            path: 'reports/sales-return',
            element: withSuspense(<SalesReturnsPage />),
          },
          {
            path: 'reports/discount',
            element: withSuspense(<DiscountsPage />),
          },
          {
            path: 'reports/unbalance',
            element: withSuspense(<UnbalancePage />),
          },
          {
            path: 'reports/daily-checklist',
            element: withSuspense(<DailyChecklistsPage />),
          },
          {
            path: 'reports/daily-checklist/area-manager-report',
            element: withSuspense(<AreaManagerReportPage />),
          },
          {
            path: 'reports/daily-checklist/area-manager-report/tasks',
            element: withSuspense(<AreaManagerReportPage />),
          },
          {
            path: 'reports/driver-checklist',
            element: withSuspense(<DriverChecklistsPage />),
          },
          {
            path: 'reports/petty-cash',
            element: withSuspense(<PettyCashPage />),
          },
          {
            path: 'reports/staff-evaluation',
            element: withSuspense(<StaffEvaluationsPage />),
          },
          {
            path: 'reports/stock-check',
            element: withSuspense(<StockChecksPage />),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: withSuspense(<NotFoundPage />),
  },
]);
